<template>
    <div class="min-h-screen bg-secondary-50 dark:bg-secondary-900">
        <!-- Impersonation Banner (always at top if impersonating) -->
        <ImpersonationBanner :impersonation-data="$page.props.impersonation" />

        <!-- Top Navigation Bar -->
        <nav class="fixed top-0 z-50 w-full bg-white dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700 shadow-sm">
            <div class="px-3 py-3 lg:px-5 lg:pl-3">
                <div class="flex items-center justify-between">
                    <!-- Left: Logo + Sidebar Toggle -->
                    <div class="flex items-center justify-start">
                        <!-- Mobile Sidebar Toggle (only show if user has company) -->
                        <button
                            v-if="hasCompany"
                            @click="toggleSidebar"
                            type="button"
                            class="inline-flex items-center p-2 text-sm text-secondary-500 rounded-lg lg:hidden hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-200 dark:text-secondary-400 dark:hover:bg-secondary-700 dark:focus:ring-secondary-600"
                            aria-label="Toggle sidebar"
                        >
                            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                <path clip-rule="evenodd" fill-rule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"></path>
                            </svg>
                        </button>

                        <!-- Logo -->
                        <Link :href="hasCompany ? '/dashboard' : '/add-first-company'" class="flex items-center ml-2 lg:ml-0 space-x-3">
                            <div class="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <span class="self-center text-xl font-semibold whitespace-nowrap dark:text-white hidden sm:block">
                                {{ appName }}
                            </span>
                        </Link>

                        <!-- Desktop Sidebar Toggle (only show if user has company) -->
                        <button
                            v-if="hasCompany"
                            @click="toggleSidebarCollapse"
                            type="button"
                            class="hidden lg:inline-flex items-center p-2 ml-3 text-sm text-secondary-500 rounded-lg hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-200 dark:text-secondary-400 dark:hover:bg-secondary-700 dark:focus:ring-secondary-600"
                            aria-label="Toggle sidebar collapse"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                            </svg>
                        </button>
                    </div>

                    <!-- Center: Search Bar (Desktop) - only show if user has company -->
                    <div v-if="hasCompany" class="hidden md:block flex-1 max-w-2xl mx-4">
                        <div class="relative">
                            <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                <svg class="w-5 h-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="search"
                                v-model="searchQuery"
                                @keyup.enter="performSearch"
                                class="block w-full p-2 pl-10 text-sm text-secondary-900 border border-secondary-300 rounded-lg bg-secondary-50 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:border-secondary-600 dark:placeholder-secondary-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                                placeholder="Search tickets, assets, users... (Ctrl+K)"
                            />
                        </div>
                    </div>

                    <!-- Right: Company Name, Notifications, User Menu, Dark Mode -->
                    <div class="flex items-center space-x-2">
                        <!-- Current Company Name (always visible) -->
                        <div v-if="currentCompany" class="hidden sm:flex items-center px-3 py-2 bg-secondary-100 dark:bg-secondary-700 rounded-lg border border-secondary-200 dark:border-secondary-600">
                            <div class="w-6 h-6 bg-primary-600 text-white rounded flex items-center justify-center text-xs font-semibold mr-2">
                                {{ currentCompany?.name?.charAt(0) || 'C' }}
                            </div>
                            <span class="text-sm font-medium text-secondary-700 dark:text-secondary-300 whitespace-nowrap">
                                {{ currentCompany?.name }}
                            </span>
                        </div>

                        <!-- Notifications (Phase 8) -->
                        <NotificationCenter />

                        <!-- Dark Mode Toggle -->
                        <button
                            @click="toggleDarkMode"
                            type="button"
                            class="p-2 text-secondary-500 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-secondary-200 dark:focus:ring-secondary-600"
                            aria-label="Toggle dark mode"
                        >
                            <svg v-if="isDark" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        </button>

                        <!-- User Menu -->
                        <div class="relative">
                            <button
                                @click="toggleUserMenu"
                                type="button"
                                class="flex items-center space-x-2 p-1 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-secondary-200 dark:focus:ring-secondary-600"
                                aria-label="User menu"
                            >
                                <div class="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                                    {{ userInitials }}
                                </div>
                                <svg class="w-4 h-4 text-secondary-500 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            <!-- User Dropdown -->
                            <div
                                v-show="userMenuOpen"
                                @click.away="userMenuOpen = false"
                                class="absolute right-0 mt-2 w-64 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-secondary-200 dark:border-secondary-700 py-1 z-50"
                            >
                                <div class="px-4 py-3 border-b border-secondary-200 dark:border-secondary-700">
                                    <p class="text-sm font-medium text-secondary-900 dark:text-white">{{ user?.first_name }} {{ user?.last_name }}</p>
                                    <p class="text-xs text-secondary-500 dark:text-secondary-400 truncate">{{ user?.email }}</p>
                                </div>
                                <Link href="/profile" class="flex items-center space-x-2 px-4 py-2 hover:bg-secondary-50 dark:hover:bg-secondary-700 text-sm text-secondary-700 dark:text-secondary-300">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    <span>Profile</span>
                                </Link>
                                <!-- My Companies - only show if user has company -->
                                <Link v-if="hasCompany" href="/my-companies" class="flex items-center space-x-2 px-4 py-2 hover:bg-secondary-50 dark:hover:bg-secondary-700 text-sm text-secondary-700 dark:text-secondary-300">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                    <span>My Companies</span>
                                </Link>
                                <div class="border-t border-secondary-200 dark:border-secondary-700 my-1"></div>
                                <Link
                                    href="/api/auth/logout"
                                    method="post"
                                    as="button"
                                    type="button"
                                    @before="handleLogout"
                                    class="w-full flex items-center space-x-2 px-4 py-2 hover:bg-secondary-50 dark:hover:bg-secondary-700 text-sm text-danger-600 dark:text-danger-400"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                    <span>Sign Out</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </nav>

        <!-- Company Status Banners -->
        <div class="fixed top-16 left-0 right-0 z-30">
            <!-- Suspended/Cancelled Company - Owner View -->
            <div
                v-if="companyStatus === 'suspended' || companyStatus === 'cancelled'"
                class="bg-gradient-to-r from-red-600 to-red-700 border-b border-red-800 shadow-lg"
            >
                <div class="max-w-7xl mx-auto px-4 py-3">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center space-x-3">
                            <div class="flex-shrink-0">
                                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div>
                                <p class="text-sm font-semibold text-white">
                                    Company {{ companyStatus === 'suspended' ? 'Suspended' : 'Cancelled' }}
                                </p>
                                <p class="text-xs text-red-100">
                                    Your subscription has expired. Reactivate to restore full access for your team.
                                </p>
                            </div>
                        </div>
                        <Link
                            :href="`/billing/reactivate?company_id=${currentCompany?.id}`"
                            class="flex-shrink-0 px-4 py-2 bg-white text-red-700 rounded-lg font-semibold text-sm hover:bg-red-50 transition-colors shadow-md"
                        >
                            Reactivate Subscription
                        </Link>
                    </div>
                </div>
            </div>

            <!-- Subscription Ending Soon (14 days or less, only if ends_at is set) -->
            <div
                v-else-if="companyStatus === 'active' && subscriptionEndsAt && subscriptionDaysRemaining !== null && subscriptionDaysRemaining <= 14"
                class="bg-gradient-to-r from-yellow-600 to-yellow-700 border-b border-yellow-800 shadow-lg"
            >
                <div class="max-w-7xl mx-auto px-4 py-3">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center space-x-3">
                            <div class="flex-shrink-0">
                                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <p class="text-sm font-semibold text-white">
                                    Subscription Ending Soon
                                </p>
                                <p class="text-xs text-yellow-100">
                                    Your subscription will end in {{ subscriptionDaysRemaining }} {{ subscriptionDaysRemaining === 1 ? 'day' : 'days' }} on {{ formatDate(subscriptionEndsAt) }}.
                                </p>
                            </div>
                        </div>
                        <Link
                            href="/billing"
                            class="flex-shrink-0 px-4 py-2 bg-white text-yellow-700 rounded-lg font-semibold text-sm hover:bg-yellow-50 transition-colors shadow-md"
                        >
                            Renew Now
                        </Link>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sidebar - only show if user has company -->
        <aside
            v-if="hasCompany"
            :class="[
                'fixed left-0 top-0 z-40 h-screen transition-transform',
                topPadding,
                sidebarCollapsed ? 'w-16' : 'w-64',
                sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
            ]"
            class="bg-white dark:bg-secondary-800 border-r border-secondary-200 dark:border-secondary-700"
        >
            <div class="h-full px-3 pb-4 overflow-y-auto scrollbar-thin">
                <ul class="space-y-2 font-medium pt-4">
                    <!-- Dashboard -->
                    <li>
                        <Link
                            href="/dashboard"
                            class="nav-item text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3">Dashboard</span>
                        </Link>
                    </li>

                    <!-- My Tasks -->
                    <li>
                        <Link
                            :href="route('my-tasks.index')"
                            :class="isActive('/my-tasks') ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' : 'text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700'"
                            class="nav-item"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3">My Tasks</span>
                            <span
                                v-if="!sidebarCollapsed && $page.props.allTasksPendingCount > 0"
                                class="ml-auto inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-red-600 dark:bg-red-500 rounded-full"
                            >
                                {{ $page.props.allTasksPendingCount }}
                            </span>
                        </Link>
                    </li>

                    <!-- Divider -->
                    <li v-if="!sidebarCollapsed" class="pt-4 pb-2">
                        <span class="text-xs font-semibold text-secondary-400 uppercase tracking-wider">Quick Actions</span>
                    </li>

                    <!-- Request IT Support -->
                    <li>
                        <Link
                            href="/tickets/create"
                            class="nav-item bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-900/50"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3 font-medium">Request IT Support</span>
                        </Link>
                    </li>

                    <!-- Request Maintenance -->
                    <li>
                        <Link
                            href="/work-orders/create"
                            class="nav-item bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300 dark:hover:bg-green-900/50"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3 font-medium">Request Maintenance</span>
                        </Link>
                    </li>

                    <!-- Report Incident -->
                    <li>
                        <Link
                            href="/incidents/create"
                            class="nav-item bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/50"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3 font-medium">Report Incident</span>
                        </Link>
                    </li>

                    <!-- Divider -->
                    <li v-if="!sidebarCollapsed" class="pt-4 pb-2">
                        <span class="text-xs font-semibold text-secondary-400 uppercase tracking-wider">Modules</span>
                    </li>

                    <!-- External Requests -->
                    <li v-if="hasExternalRequestsPermission">
                        <Link
                            href="/external-requests"
                            :class="isActive('/external-requests')"
                            class="nav-item"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3">External Requests</span>
                        </Link>
                    </li>

                    <!-- IT Helpdesk -->
                    <li v-if="hasModule('it_helpdesk')">
                        <button
                            @click="toggleItHelpdeskDropdown"
                            type="button"
                            :class="isActive('/tickets') || isActive('/tickets/sla-dashboard') || isActive('/settings/it-equipment') || isActive('/settings/tickets')"
                            class="nav-item w-full"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3 flex-1 text-left">IT Helpdesk</span>
                            <svg v-if="!sidebarCollapsed" :class="{ 'rotate-180': itHelpdeskDropdownOpen }" class="w-5 h-5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        <!-- IT Helpdesk Submenu -->
                        <ul v-show="itHelpdeskDropdownOpen && !sidebarCollapsed" class="pl-8 mt-2 space-y-2">
                            <li>
                                <Link
                                    href="/tickets"
                                    :class="isActive('/tickets')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                    </svg>
                                    <span class="ml-2">Tickets</span>
                                </Link>
                            </li>
                            <li v-if="auth.checkPermission('it_helpdesk.reports.view')">
                                <Link
                                    href="/tickets/sla-dashboard"
                                    :class="isActive('/tickets/sla-dashboard')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <span class="ml-2">Performance</span>
                                </Link>
                            </li>
                            <li v-if="subscriptionFeatures.equipment_module">
                                <Link
                                    href="/settings/it-equipment"
                                    :class="isActive('/settings/it-equipment')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <span class="ml-2">IT Equipment</span>
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/settings/tickets"
                                    :class="isExactPath('/settings/tickets') ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' : 'text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700'"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                    </svg>
                                    <span class="ml-2">Settings</span>
                                </Link>
                            </li>
                        </ul>
                    </li>

                    <!-- Maintenance -->
                    <li v-if="hasModule('maintenance')">
                        <button
                            @click="toggleMaintenanceDropdown"
                            type="button"
                            :class="isActive('/work-orders') || isActive('/pm-schedules') || isActive('/maintenance/sla-dashboard') || isActive('/settings/maintenance')"
                            class="nav-item w-full"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3 flex-1 text-left">Maintenance</span>
                            <svg v-if="!sidebarCollapsed" :class="{ 'rotate-180': maintenanceDropdownOpen }" class="w-5 h-5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        <!-- Maintenance Submenu -->
                        <ul v-show="maintenanceDropdownOpen && !sidebarCollapsed" class="pl-8 mt-2 space-y-2">
                            <li>
                                <Link
                                    href="/work-orders"
                                    :class="isActive('/work-orders')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                    </svg>
                                    <span class="ml-2">Work Orders</span>
                                </Link>
                            </li>
                            <li v-if="auth.checkPermission('maintenance.reports.view')">
                                <Link
                                    href="/maintenance/sla-dashboard"
                                    :class="isActive('/maintenance/sla-dashboard')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <span class="ml-2">Performance</span>
                                </Link>
                            </li>
                            <li v-if="subscriptionFeatures.asset_module">
                                <Link
                                    href="/pm-schedules"
                                    :class="isActive('/pm-schedules')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span class="ml-2">PM Schedules</span>
                                </Link>
                            </li>
                            <li v-if="subscriptionFeatures.asset_module">
                                <Link
                                    href="/settings/maintenance/assets"
                                    :class="isActive('/settings/maintenance/assets')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                    <span class="ml-2">Assets</span>
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/settings/maintenance"
                                    :class="isExactPath('/settings/maintenance') ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' : 'text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700'"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                    </svg>
                                    <span class="ml-2">All Settings</span>
                                </Link>
                            </li>
                        </ul>
                    </li>

                    <!-- Incidents -->
                    <li v-if="hasModule('incidents')">
                        <button
                            @click="toggleIncidentsDropdown"
                            type="button"
                            :class="isActive('/incidents') || isActive('/settings/incidents') || isActive('/incidents/sla-dashboard')"
                            class="nav-item w-full"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3 flex-1 text-left">Incidents</span>
                            <svg v-if="!sidebarCollapsed" :class="{ 'rotate-180': incidentsDropdownOpen }" class="w-5 h-5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        <!-- Incidents Submenu -->
                        <ul v-show="incidentsDropdownOpen && !sidebarCollapsed" class="pl-8 mt-2 space-y-2">
                            <li>
                                <Link
                                    href="/incidents"
                                    :class="isActive('/incidents') && !isActive('/settings/incidents') && !isActive('/incidents/sla-dashboard')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <span class="ml-2">Incidents</span>
                                </Link>
                            </li>
                            <li v-if="auth.checkPermission('incidents.reports.view')">
                                <Link
                                    href="/incidents/sla-dashboard"
                                    :class="isActive('/incidents/sla-dashboard')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <span class="ml-2">Performance</span>
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/settings/incidents"
                                    :class="isExactPath('/settings/incidents') ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' : 'text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700'"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                    </svg>
                                    <span class="ml-2">Settings</span>
                                </Link>
                            </li>
                        </ul>
                    </li>

                    <!-- Inspection & Checklist -->
                    <li v-if="hasModule('checklists')">
                        <button
                            @click="toggleChecklistsDropdown"
                            type="button"
                            :class="isActive('/checklists')"
                            class="nav-item w-full"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3 flex-1 text-left">Inspection</span>
                            <svg v-if="!sidebarCollapsed" :class="{ 'rotate-180': checklistsDropdownOpen }" class="w-5 h-5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        <!-- Checklists Submenu -->
                        <ul v-show="checklistsDropdownOpen && !sidebarCollapsed" class="pl-8 mt-2 space-y-2">
                            <li>
                                <Link
                                    href="/checklists"
                                    :class="isExactPath('/checklists') ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' : 'text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700'"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                    <span class="ml-2">Checklists</span>
                                </Link>
                            </li>
                            <li v-if="auth.checkPermission('checklists.reports.view')">
                                <Link
                                    :href="route('checklists.performance')"
                                    :class="isExactPath('/checklists/performance') ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' : 'text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700'"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <span class="ml-2">Performance</span>
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/checklists/schedules"
                                    :class="isExactPath('/checklists/schedules') ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' : 'text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700'"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span class="ml-2">Schedules</span>
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/checklists/templates"
                                    :class="isExactPath('/checklists/templates') ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' : 'text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700'"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                    </svg>
                                    <span class="ml-2">Templates</span>
                                </Link>
                            </li>
                        </ul>
                    </li>

                    <!-- Reports -->
                    <li v-if="hasAnyReportPermission">
                        <button
                            @click="toggleReportsDropdown"
                            type="button"
                            :class="isActive('/reports') || isActive('/reports/tickets') || isActive('/reports/maintenance') || isActive('/reports/incidents')"
                            class="nav-item w-full"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3 flex-1 text-left">Reports</span>
                            <svg v-if="!sidebarCollapsed" :class="{ 'rotate-180': reportsDropdownOpen }" class="w-5 h-5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        <!-- Reports Submenu -->
                        <ul v-show="reportsDropdownOpen && !sidebarCollapsed" class="pl-8 mt-2 space-y-2">
                            <li v-if="auth.hasAllPermissions(['it_helpdesk.reports.view', 'maintenance.reports.view', 'incidents.reports.view'])">
                                <Link
                                    href="/reports"
                                    :class="isExactPath('/reports') ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' : 'text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700'"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <span class="ml-2">Summary</span>
                                </Link>
                            </li>
                            <li v-if="hasModule('it_helpdesk') && auth.checkPermission('it_helpdesk.reports.view')">
                                <Link
                                    href="/reports/tickets"
                                    :class="isActive('/reports/tickets')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                    </svg>
                                    <span class="ml-2">IT Help Desk</span>
                                </Link>
                            </li>
                            <li v-if="hasModule('maintenance') && auth.checkPermission('maintenance.reports.view')">
                                <Link
                                    href="/reports/maintenance"
                                    :class="isActive('/reports/maintenance')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span class="ml-2">Maintenance</span>
                                </Link>
                            </li>
                            <li v-if="hasModule('incidents') && auth.checkPermission('incidents.reports.view')">
                                <Link
                                    href="/reports/incidents"
                                    :class="isActive('/reports/incidents')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <span class="ml-2">Incidents</span>
                                </Link>
                            </li>
                            <li v-if="hasModule('checklists') && auth.hasAnyPermission(['checklists.reports.view', 'checklists.reports.view_own'])">
                                <Link
                                    href="/reports/checklists"
                                    :class="isActive('/reports/checklists')"
                                    class="nav-item text-sm"
                                >
                                    <svg class="nav-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                    </svg>
                                    <span class="ml-2">Inspection</span>
                                </Link>
                            </li>
                        </ul>
                    </li>

                    <!-- Divider -->
                    <li v-if="!sidebarCollapsed" class="pt-4 pb-2">
                        <span class="text-xs font-semibold text-secondary-400 uppercase tracking-wider">System</span>
                    </li>

                    <!-- Settings -->
                    <li>
                        <Link
                            href="/settings"
                            :class="isActive('/settings')"
                            class="nav-item"
                        >
                            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span v-if="!sidebarCollapsed" class="ml-3">Settings</span>
                        </Link>
                    </li>
                </ul>
            </div>
        </aside>

        <!-- Mobile Sidebar Overlay -->
        <div
            v-if="sidebarOpen"
            @click="closeSidebar"
            class="fixed inset-0 z-30 bg-secondary-900/50 lg:hidden"
        ></div>

        <!-- Main Content -->
        <main
            :class="[
                'transition-all',
                topPadding,
                hasCompany ? (sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64') : ''
            ]"
        >
            <div class="p-4 sm:p-6 lg:p-8">
                <!-- Breadcrumbs -->
                <nav v-if="breadcrumbs && breadcrumbs.length > 0" class="flex mb-6" aria-label="Breadcrumb">
                    <ol class="inline-flex items-center space-x-1 md:space-x-3">
                        <li class="inline-flex items-center">
                            <Link href="/dashboard" class="inline-flex items-center text-sm font-medium text-secondary-700 hover:text-primary-600 dark:text-secondary-400 dark:hover:text-white">
                                <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                                </svg>
                                Home
                            </Link>
                        </li>
                        <li v-for="(crumb, index) in breadcrumbs" :key="index">
                            <div class="flex items-center">
                                <svg class="w-6 h-6 text-secondary-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                                </svg>
                                <Link
                                    v-if="crumb.url"
                                    :href="crumb.url"
                                    class="ml-1 text-sm font-medium text-secondary-700 hover:text-primary-600 md:ml-2 dark:text-secondary-400 dark:hover:text-white"
                                >
                                    {{ crumb.label }}
                                </Link>
                                <span v-else class="ml-1 text-sm font-medium text-secondary-500 md:ml-2 dark:text-secondary-400">
                                    {{ crumb.label }}
                                </span>
                            </div>
                        </li>
                    </ol>
                </nav>

                <!-- Page Content -->
                <slot />
            </div>
        </main>

        <!-- Toast Notifications -->
        <Toast />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { Link, router, usePage } from '@inertiajs/vue3';
import NotificationCenter from '@/Components/Notifications/NotificationCenter.vue';
import ImpersonationBanner from '@/Components/Layout/ImpersonationBanner.vue';
import Toast from '@/Components/Common/Toast.vue';
import { useToast } from '@/Composables/useToast';
import { useAuth } from '@/Composables/useAuth';

// Page props
const page = usePage();
const auth = useAuth();
const appName = computed(() => page.props.app?.name || 'Margin Resolve');
const user = computed(() => page.props.auth?.user || null);
const currentCompany = computed(() => page.props.auth?.company || null);
const companies = computed(() => page.props.auth?.companies || []);
const breadcrumbs = computed(() => page.props.breadcrumbs || []);
const activeModules = computed(() => page.props.activeModules || []);
const subscriptionFeatures = computed(() => page.props.subscriptionFeatures || { asset_module: false, equipment_module: false });

// Sidebar state
const sidebarOpen = ref(false);
const sidebarCollapsed = ref(false);

// Dropdown states
const userMenuOpen = ref(false);
const itHelpdeskDropdownOpen = ref(false);
const maintenanceDropdownOpen = ref(false);
const incidentsDropdownOpen = ref(false);
const checklistsDropdownOpen = ref(false);
const reportsDropdownOpen = ref(false);

// Search
const searchQuery = ref('');

// Dark mode
const isDark = ref(false);

// Toast notifications
const toast = useToast();

// Computed
const userInitials = computed(() => {
    if (!user.value) return 'U';
    const first = user.value.first_name?.charAt(0) || '';
    const last = user.value.last_name?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
});

// Check if user has any company (for conditional rendering)
const hasCompany = computed(() => page.props.auth?.hasCompany || false);

// Company Status for banners
const companyStatus = computed(() => currentCompany.value?.company_status || null);

// Subscription ends at (only for subscriptions with ends_at set - cancelled subscriptions)
const subscriptionEndsAt = computed(() => {
    return currentCompany.value?.subscription?.ends_at || null;
});

// Subscription days remaining (only when ends_at is set)
const subscriptionDaysRemaining = computed(() => {
    if (!subscriptionEndsAt.value) return null;
    const endsAt = new Date(subscriptionEndsAt.value);
    const now = new Date();
    const diffTime = endsAt - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
});

// Check if any status banner is showing
const hasStatusBanner = computed(() => {
    // Suspended/Cancelled banner
    if (companyStatus.value === 'suspended' || companyStatus.value === 'cancelled') {
        return true;
    }
    // Subscription ending soon
    if (companyStatus.value === 'active' && subscriptionEndsAt.value && subscriptionDaysRemaining.value !== null && subscriptionDaysRemaining.value <= 14) {
        return true;
    }
    return false;
});

// Dynamic top padding for sidebar and main content (nav + banner)
const topPadding = computed(() => {
    return hasStatusBanner.value ? 'pt-28' : 'pt-16';
});

// Methods
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};
const toggleSidebar = () => {
    sidebarOpen.value = !sidebarOpen.value;
};

const closeSidebar = () => {
    sidebarOpen.value = false;
};

const toggleSidebarCollapse = () => {
    sidebarCollapsed.value = !sidebarCollapsed.value;
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed.value);
};

const toggleUserMenu = () => {
    userMenuOpen.value = !userMenuOpen.value;
};

const toggleItHelpdeskDropdown = () => {
    itHelpdeskDropdownOpen.value = !itHelpdeskDropdownOpen.value;
};

const toggleMaintenanceDropdown = () => {
    maintenanceDropdownOpen.value = !maintenanceDropdownOpen.value;
};

const toggleIncidentsDropdown = () => {
    incidentsDropdownOpen.value = !incidentsDropdownOpen.value;
};

const toggleChecklistsDropdown = () => {
    checklistsDropdownOpen.value = !checklistsDropdownOpen.value;
};

const toggleReportsDropdown = () => {
    reportsDropdownOpen.value = !reportsDropdownOpen.value;
};

const toggleDarkMode = () => {
    isDark.value = !isDark.value;
    if (isDark.value) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('theme', 'dark');
    } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme', 'light');
    }
};

const performSearch = () => {
    if (searchQuery.value.trim()) {
        router.get('/search', { q: searchQuery.value });
    }
};

const isActive = (path) => {
    const currentPath = window.location.pathname;
    const isActiveRoute = currentPath === path || currentPath.startsWith(path + '/');
    return isActiveRoute
        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
        : 'text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700';
};

const hasModule = (moduleName) => {
    return activeModules.value.includes(moduleName);
};

// Check if user has any external requests permissions
const hasExternalRequestsPermission = computed(() => {
    const permissions = page.props.auth?.permissions || [];
    return permissions.includes('external_requests.view') ||
           permissions.includes('external_requests.convert') ||
           permissions.includes('external_requests.close');
});

// Check if user has any reports permissions
const hasAnyReportPermission = computed(() => {
    return auth.hasAnyPermission([
        'it_helpdesk.reports.view',
        'maintenance.reports.view',
        'incidents.reports.view',
        'checklists.reports.view',
        'checklists.reports.view_own'
    ]);
});

const isExactPath = (path) => {
    const currentPath = page.url;
    return currentPath === path;
};

const handleLogout = () => {
    // Set a flag in sessionStorage to indicate logout is in progress
    // This will be used by the error handler to suppress error alerts during logout
    sessionStorage.setItem('is_logging_out', 'true');

    // Clear the flag after 5 seconds as a safety measure
    // In case something goes wrong and the user doesn't get redirected
    setTimeout(() => {
        sessionStorage.removeItem('is_logging_out');
    }, 5000);
};

// Keyboard shortcuts
const handleKeyboard = (event) => {
    // Ctrl+K or Cmd+K for search
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        document.querySelector('input[type="search"]')?.focus();
    }

    // Escape to close dropdowns
    if (event.key === 'Escape') {
        userMenuOpen.value = false;
    }
};

// Watch for route changes to auto-open/close IT helpdesk dropdown
watch(() => page.url, (newUrl) => {
    const currentPath = newUrl;
    // If navigating to an IT helpdesk page, open the dropdown
    if (currentPath.includes('/tickets') || currentPath.includes('/settings/it-equipment') || currentPath.includes('/settings/tickets')) {
        itHelpdeskDropdownOpen.value = true;
    } else {
        itHelpdeskDropdownOpen.value = false;
    }
});

// Watch for route changes to auto-close maintenance dropdown when navigating away
watch(() => page.url, (newUrl) => {
    const currentPath = newUrl;
    // If navigating to a non-maintenance page, close the dropdown
    if (!currentPath.includes('/work-orders') && !currentPath.includes('/pm-schedules') && !currentPath.includes('/settings/maintenance')) {
        maintenanceDropdownOpen.value = false;
    } else {
        // If navigating to a maintenance page, open the dropdown
        maintenanceDropdownOpen.value = true;
    }
});

// Watch for route changes to auto-open/close incidents dropdown
watch(() => page.url, (newUrl) => {
    const currentPath = newUrl;
    // If navigating to an incidents page, open the dropdown
    if (currentPath.includes('/incidents') || currentPath.includes('/settings/incidents')) {
        incidentsDropdownOpen.value = true;
    } else {
        incidentsDropdownOpen.value = false;
    }
});

// Watch for route changes to auto-open/close checklists dropdown
watch(() => page.url, (newUrl) => {
    const currentPath = newUrl;
    // If navigating to a checklists page, open the dropdown
    if (currentPath.includes('/checklists')) {
        checklistsDropdownOpen.value = true;
    } else {
        checklistsDropdownOpen.value = false;
    }
});

// Watch for route changes to auto-open/close reports dropdown
watch(() => page.url, (newUrl) => {
    const currentPath = newUrl;
    // If navigating to a reports page, open the dropdown
    if (currentPath.includes('/reports')) {
        reportsDropdownOpen.value = true;
    } else {
        reportsDropdownOpen.value = false;
    }
});

// Watch for flash messages and display them
watch(() => page.props.flash, (flash) => {
    if (flash?.success) toast.success(flash.success);
    if (flash?.error) toast.error(flash.error);
    if (flash?.info) toast.info(flash.info);
    if (flash?.warning) toast.warning(flash.warning);
}, { immediate: true, deep: true });

// Initialize
onMounted(() => {
    // Restore sidebar collapsed state
    const savedCollapsed = localStorage.getItem('sidebarCollapsed');
    if (savedCollapsed !== null) {
        sidebarCollapsed.value = savedCollapsed === 'true';
    }

    // Initialize dark mode
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
        isDark.value = true;
        document.documentElement.classList.add('dark');
    }

    // Auto-open IT helpdesk dropdown if on an IT helpdesk page
    const currentPath = window.location.pathname;
    if (currentPath.includes('/tickets') || currentPath.includes('/settings/it-equipment') || currentPath.includes('/settings/tickets')) {
        itHelpdeskDropdownOpen.value = true;
    }

    // Auto-open maintenance dropdown if on a maintenance page
    if (currentPath.includes('/work-orders') || currentPath.includes('/pm-schedules') || currentPath.includes('/settings/maintenance')) {
        maintenanceDropdownOpen.value = true;
    }

    // Auto-open incidents dropdown if on an incidents page
    if (currentPath.includes('/incidents') || currentPath.includes('/settings/incidents')) {
        incidentsDropdownOpen.value = true;
    }

    // Auto-open reports dropdown if on a reports page
    if (currentPath.includes('/reports')) {
        reportsDropdownOpen.value = true;
    }

    // Add keyboard shortcuts
    window.addEventListener('keydown', handleKeyboard);
});
</script>

<style scoped>
@reference "../../css/app.css";

/* Navigation item base styles */
.nav-item {
    @apply flex items-center p-2 rounded-lg transition-colors;
}

.nav-icon {
    @apply flex-shrink-0 w-6 h-6;
}

/* Click outside directive for dropdowns */
/* This would typically be a Vue directive, but we're using @click.away from a library or custom implementation */
</style>
